/**
 * StageRail — the Brief → Strategy → Creative → Launch → Live pill rail.
 *
 * Completed stages are clickable (read-only view of that panel), the current
 * stage is highlighted, future stages are inert. Stage advancement itself
 * only ever happens through approve buttons — this rail is pure navigation.
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { STAGES, STAGE_LABELS, type Stage } from './types';

export interface StageRailProps {
  stage: Stage;
  viewStage: Stage;
  onViewStage: (stage: Stage) => void;
  className?: string;
}

export const StageRail: React.FC<StageRailProps> = ({
  stage,
  viewStage,
  onViewStage,
  className,
}) => {
  const currentIdx = STAGES.indexOf(stage);

  // Stage bars (campaign page board): a bar per stage with its state in words — done in green,
  // here in orange, later in a line colour. Done stages open read-only; later ones are inert.
  return (
    <nav className={cn('flex w-full gap-1.5', className)} aria-label="Campaign stages">
      {STAGES.map((s, i) => {
        const done = i < currentIdx;
        const current = i === currentIdx;
        const viewing = s === viewStage;
        const clickable = i <= currentIdx;
        const note = current ? (s === 'live' ? '● Live' : 'You’re here') : done ? '✓ Approved' : s === 'live' ? 'After launch' : 'Next';
        return (
          <button
            key={s}
            type="button"
            disabled={!clickable}
            onClick={() => clickable && onViewStage(s)}
            aria-current={current ? 'step' : undefined}
            className={cn(
              'flex min-w-0 flex-1 flex-col gap-1.5 rounded-lg border-0 bg-transparent p-0 text-left transition-colors',
              clickable ? 'hover:bg-ground' : 'cursor-default',
              viewing && !current && 'bg-ground',
            )}
          >
            <span className={cn('h-1.5 rounded-full', current ? (s === 'live' ? 'bg-ok' : 'bg-brand') : done ? 'bg-ok' : 'bg-line-2')} />
            <span className={cn('hidden truncate text-xs font-semibold md:block', current ? 'text-brand-ink' : done ? 'text-ok' : 'text-ink-3')}>{note}</span>
            <span className={cn('truncate font-display text-[13px] md:text-[15px]', current ? 'font-extrabold text-ink' : clickable ? 'font-bold text-ink' : 'font-bold text-ink-2')}>{STAGE_LABELS[s]}</span>
          </button>
        );
      })}
    </nav>
  );
};
